import {
  type UiStyle,
  isStyleAvailable,
} from "../../shared/design/design.types";

import {
  useDesign,
} from "../../shared/design/DesignProvider";

import {
  useTheme,
  type ColourTheme,
} from "../../context/ThemeContext";

import "./AppearancePicker.css";

const colourModes: Array<{
  id: ColourTheme;
  title: string;
  description: string;
}> = [
  {
    id: "SYSTEM",
    title: "System",
    description: "Follow your phone or computer.",
  },
  {
    id: "LIGHT",
    title: "Light",
    description: "Always use the bright appearance.",
  },
  {
    id: "DARK",
    title: "Dark",
    description: "Always use the dark appearance.",
  },
];

const styles: Array<{
  id: UiStyle;
  title: string;
  description: string;
  requiredPlan: string;
}> = [
  {
    id: "SPATIAL_FLOW",
    title: "Spatial Flow",
    description:
      "Clean layered surfaces for everyday planning.",
    requiredPlan: "Included with SARADIUM Free",
  },
  {
    id: "SOFT_NEUMORPHISM",
    title: "Soft Neumorphism",
    description:
      "Solid, quietly carved surfaces and soft depth.",
    requiredPlan: "Available with SARADIUM Premium",
  },
  {
    id: "LIQUID_GLASS",
    title: "Liquid Glass",
    description:
      "Water-like fixed glass with moving palettes behind it.",
    requiredPlan:
      "Available with SARADIUM Premium Plus",
  },
];

export function AppearancePicker() {
  const {
    plan,
    developer,
    style: selectedStyle,
    liquidPalette,
    setStyle,
    setLiquidPalette,
  } = useDesign();

  const {
    colourTheme,
    setColourTheme,
  } = useTheme();

  function unlocked(style: UiStyle): boolean {
    return (
      developer ||
      isStyleAvailable(style, plan)
    );
  }

  function chooseStyle(style: UiStyle) {
    if (!unlocked(style)) {
      window.alert(
        style === "SOFT_NEUMORPHISM"
          ? "Soft Neumorphism is available with SARADIUM Premium."
          : "Liquid Glass is available with SARADIUM Premium Plus.",
      );

      return;
    }

    setStyle(style);
  }

  const liquidAvailable = unlocked(
    "LIQUID_GLASS",
  );

  return (
    <section className="appearance-picker">
      <div className="appearance-picker__heading">
        <span>APPEARANCE</span>

        <h2>Your SARADIUM design</h2>

        <p>
          Choose a colour mode first, then choose one
          interface design. Designs never overlap.
        </p>
      </div>

      <section className="appearance-picker__section">
        <div className="appearance-picker__section-heading">
          <span>COLOUR MODE</span>

          <p>
            System follows your device automatically.
          </p>
        </div>

        <div className="appearance-picker__modes">
          {colourModes.map((mode) => (
            <button
              className={
                colourTheme === mode.id
                  ? "appearance-picker__mode appearance-picker__mode--selected"
                  : "appearance-picker__mode"
              }
              key={mode.id}
              type="button"
              onClick={() => setColourTheme(mode.id)}
            >
              <strong>{mode.title}</strong>

              <small>{mode.description}</small>
            </button>
          ))}
        </div>
      </section>

      <section className="appearance-picker__section">
        <div className="appearance-picker__section-heading">
          <span>INTERFACE DESIGN</span>

          <p>
            Your subscription unlocks available
            designs.
          </p>
        </div>

        <div className="appearance-picker__styles">
          {styles.map((item) => {
            const available = unlocked(item.id);

            return (
              <button
                className={
                  selectedStyle === item.id
                    ? "appearance-picker__style appearance-picker__style--selected"
                    : "appearance-picker__style"
                }
                key={item.id}
                type="button"
                onClick={() => chooseStyle(item.id)}
              >
                <span
                  className={
                    "appearance-picker__preview " +
                    `appearance-picker__preview--${item.id.toLowerCase()}`
                  }
                />

                <strong>{item.title}</strong>

                <small>{item.description}</small>

                <em>
                  {available
                    ? selectedStyle === item.id
                      ? "Selected"
                      : "Available"
                    : item.requiredPlan}
                </em>
              </button>
            );
          })}
        </div>
      </section>

      {selectedStyle === "LIQUID_GLASS" &&
        liquidAvailable && (
          <section className="appearance-picker__palettes">
            <div className="appearance-picker__section-heading">
              <span>LIQUID GLASS PALETTE</span>

              <p>
                This changes only the water behind
                your colourless glass surfaces.
              </p>
            </div>

            <div className="appearance-picker__palette-row">
              {[
                ["AURORA", "Aurora"],
                ["CYAN_LAGOON", "Cyan Lagoon"],
                ["COFFEE_MILK", "Coffee Milk"],
              ].map(([id, label]) => (
                <button
                  className={
                    liquidPalette === id
                      ? "appearance-picker__palette appearance-picker__palette--selected"
                      : "appearance-picker__palette"
                  }
                  key={id}
                  type="button"
                  onClick={() =>
                    setLiquidPalette(
                      id as
                        | "AURORA"
                        | "CYAN_LAGOON"
                        | "COFFEE_MILK",
                    )
                  }
                >
                  <i
                    className={
                      "appearance-picker__palette-dot " +
                      `appearance-picker__palette-dot--${id.toLowerCase()}`
                    }
                  />

                  {label}
                </button>
              ))}
            </div>
          </section>
        )}
    </section>
  );
}